import { useId } from 'react'
import { useStrictLang } from './design/strictLangContext'
import { blockTheme, hsl } from '@teach/ui'
import type { ArticleBlock } from '@/types/blocks'
import type { Instruction } from '@/features/instructions/types'
import type { ContentLang } from '@/features/editor/editorSlice'
import type { PreviewRun } from '../../preview/previewRun'
import { getLocalizedDisplay } from '@/features/editor/lib/localizedField'
import { ARTICLE_TITLE_STYLE, articleScopeStyles } from '@/features/editor/lib/articleTypography'
import { blockBackground } from './blockBackground'
import { PreviewShell } from './PreviewShell'
import { CanvasArticleChooser } from './CanvasArticleChooser'
import { useCanvasVideoDrop } from './canvasVideoDrop'
import { pickMessages, useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  block: ArticleBlock
  contentLang: ContentLang
  /** Прохождение: нижняя кнопка ведёт на следующий экран. */
  run?: PreviewRun
  /** Выбор статьи в пустом блоке на холсте (`renderBlockPreview`); нет — список погашен. */
  onArticlePick?: (articleId: number, article: Instruction) => void
}

// Показываем ВЕСЬ контент статьи (заголовок + тело), а не обложку-карточку.
// `Instruction.content` — HTML из TipTap; статьи заводит только админ, поэтому
// innerHTML здесь доверенный. Вёрстка — `articleTypography` (порт плеера).
const SKELETON_LINES = ['92%', '100%', '84%', '96%', '70%', '100%', '88%', '60%']

/** Строки-заглушки текста статьи, цветом темы урока (холст светлый). */
const ArticleBodySkeleton = () => (
  <div className="mt-4 space-y-3" aria-hidden>
    {SKELETON_LINES.map((w, i) => (
      <div
        // Неподвижный список заглушек: индекс и есть идентичность.
        key={i}
        className="h-3.5 animate-pulse rounded-full"
        style={{ width: w, backgroundColor: hsl(blockTheme.foreground, 0.08) }}
      />
    ))}
  </div>
)

export const ArticleBlockPreview = ({ block, contentLang, run, onArticlePick }: Props) => {
  const m = useMessages(messages)
  // Экран ученика: кнопки и заголовки — на языке курса, как в плеере.
  const p = pickMessages(messages, contentLang)
  // Холст и миниатюры редактора — строго язык правки; прохождение — как плеер.
  const strictLang = useStrictLang() && (!run || run.onCanvas === true)
  const scope = `article-${useId().replace(/:/g, '')}`
  const article = block.article
  const canvasEditable = useCanvasVideoDrop()

  // Блок статьи без статьи (`articleId: null`, PRD #71, ф.3) — настоящий блок
  // сервера: выбор прямо на холсте редактора, и после перезагрузки тоже. Статья
  // выбрана, но бэк её не отдал (снята с публикации), — это не пустой блок, ниже.
  if (block.articleId == null && canvasEditable && !run) {
    return (
      // Фон урока и узор — как у готового экрана статьи.
      <PreviewShell style={{ background: blockBackground(block) }} contentClassName="p-5">
        <CanvasArticleChooser onPick={onArticlePick} />
      </PreviewShell>
    )
  }

  if (!article) {
    return (
      <PreviewShell
        // Без кнопки прохождение упёрлось бы в экран без выхода: у статьи,
        // которую бэк не отдал, содержимого нет, но экран в уроке есть.
        cta={run ? p.next : undefined}
        onCta={run?.onCta}
        style={{ background: blockBackground(block) }}
        contentClassName="items-center justify-center p-8"
      >
        {/* Прохождение: ученик видит пустой экран с фоном урока, а не авторскую
            подсказку «Статья не выбрана» — расхождение с обещанным экраном ученика. */}
        {run ? null : (
          <p className="text-sm" style={{ color: hsl(blockTheme.foreground, 0.5) }}>
            {m.noArticle}
          </p>
        )}
      </PreviewShell>
    )
  }

  const title = getLocalizedDisplay(article, 'title', contentLang, strictLang)
  const content = getLocalizedDisplay(article, 'content', contentLang, strictLang)
  // Только что выбранная статья приходит из списка пикера БЕЗ текста (`content`
  // нет ни на одном языке) — текст доедет с сохранённым блоком. До тех пор —
  // скелет строк, а не пустой экран под заголовком.
  const bodyPending = !run && article.content == null && article.contentEn == null

  return (
    <PreviewShell
      cta={p.next}
      onCta={run?.onCta}
      style={{ background: blockBackground(block) }}
      // Плеер: `.scrollArea { padding: 32px 20px 96px }` — нижний отступ там под
      // растворяющуюся кромку и кнопку, у превью её роль играет CTA-полоса.
      contentClassName="flex-col overflow-y-auto px-5 pt-8"
    >
      {/* font-display = Inter: плеер верстает статью Inter'ом, а не шрифтом
          дизайн-блока (Madefor), и наследование от корня канваса тут не годится. */}
      <div className="font-display">
        <style>{articleScopeStyles(scope)}</style>
        {/* Заголовок ВНЕ скоупа: правила скоупа цепляются за теги, и `${s} h2`
            навязал бы заголовку статьи отступы и кегль подзаголовка тела. */}
        {title ? <h2 style={ARTICLE_TITLE_STYLE}>{title}</h2> : null}
        {bodyPending ? (
          <ArticleBodySkeleton />
        ) : (
          // trusted admin-authored article HTML
          <div className={scope} dangerouslySetInnerHTML={{ __html: content }} />
        )}
      </div>
    </PreviewShell>
  )
}
